import React, { useEffect, useRef } from 'react';
import { buttonReset, color, font, radius, shadow, space, weight } from '../theme';

export interface TabItem<K extends string> {
  key: K;
  label: string;
  count?: number;
}

interface TabBarProps<K extends string> {
  tabs: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  // 'segmented': equal-width segments in a track, for 2–3 tabs.
  // 'pills': a scrollable row of pills.
  // 'underline': a white tab strip of equal cells with a blue indicator under the selected
  //   tab, for a detail page's sections (MK page; up to 6 tabs on a phone).
  variant?: 'segmented' | 'pills' | 'underline';
  // Segmented only: segments sized to their labels, for a small switch beside a status line
  compact?: boolean;
  label?: string;
  style?: React.CSSProperties;
}

const tabLabel = (t: TabItem<string>) => (t.count !== undefined ? `${t.label} · ${t.count.toLocaleString('he-IL')}` : t.label);

function TabBar<K extends string>({ tabs, active, onChange, variant = 'segmented', compact = false, label, style }: TabBarProps<K>) {
  // Pills: keep the selected pill in view, scrolling only the row (never the page)
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = rowRef.current;
    if (variant !== 'pills' || !row) return;
    const el = row.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!el) return;
    const rowBox = row.getBoundingClientRect();
    const elBox = el.getBoundingClientRect();
    if (elBox.left < rowBox.left) row.scrollLeft -= rowBox.left - elBox.left + 16;
    else if (elBox.right > rowBox.right) row.scrollLeft += elBox.right - rowBox.right + 16;
  }, [active, variant, tabs.length]);

  if (variant === 'underline') {
    // Arrow keys move between tabs (RTL: Left is the next tab), as in a native tablist
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const i = tabs.findIndex(t => t.key === active);
      const next = e.key === 'Home' ? 0
        : e.key === 'End' ? tabs.length - 1
          : Math.max(0, Math.min(tabs.length - 1, i + (e.key === 'ArrowLeft' ? 1 : -1)));
      onChange(tabs[next].key);
      const el = e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next];
      if (el) el.focus();
    };
    return (
      <div role="tablist" aria-label={label} onKeyDown={onKeyDown} style={{
        display: 'flex',
        background: color.surface,
        borderRadius: radius.lg,
        // A hairline under all the tabs: the track the indicator sits on
        boxShadow: `inset 0 -1px 0 ${color.divider}, ${shadow.card}`,
        overflow: 'hidden',
        ...style,
      }}>
        {tabs.map(t => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(t.key)}
              style={{
                ...buttonReset,
                position: 'relative',
                flex: '1 1 0',
                minWidth: 0,
                minHeight: 52,
                padding: `${space.xs}px 2px`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: selected ? color.primary : color.textSecondary,
                background: selected ? 'rgba(230,241,251,0.55)' : 'transparent',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: font.label, fontWeight: selected ? weight.bold : weight.medium, lineHeight: 1.3 }}>
                {t.label}
              </span>
              {t.count !== undefined && (
                <span style={{ fontSize: font.caption, lineHeight: 1.2, color: selected ? color.primary : color.textTertiary }}>{t.count.toLocaleString('he-IL')}</span>
              )}
              <span aria-hidden="true" style={{
                position: 'absolute', bottom: 0, left: '14%', right: '14%', height: 3, borderRadius: '3px 3px 0 0',
                background: selected ? color.primary : 'transparent', transition: 'background 0.15s',
              }} />
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === 'segmented') {
    return (
      <div role="tablist" aria-label={label} style={{
        display: compact ? 'inline-flex' : 'flex',
        background: color.track,
        borderRadius: compact ? radius.md + 2 : radius.lg - 2,
        padding: compact ? 2 : 3,
        ...style,
      }}>
        {tabs.map(t => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(t.key)}
              style={{
                ...buttonReset,
                flex: compact ? '0 0 auto' : 1,
                minHeight: compact ? 36 : 38,
                padding: compact ? `0 ${space.md}px` : undefined,
                borderRadius: radius.md,
                textAlign: 'center',
                fontSize: compact ? font.small : font.label,
                fontWeight: selected ? weight.bold : weight.medium,
                color: selected ? color.primary : color.textSecondary,
                background: selected ? color.surface : 'transparent',
                boxShadow: selected ? shadow.pressed : 'none',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {tabLabel(t)}
            </button>
          );
        })}
      </div>
    );
  }

  // The visible pill is 36px; the button's vertical padding makes the tap target 44px
  return (
    <div ref={rowRef} role="tablist" aria-label={label} style={{
      display: 'flex',
      gap: space.sm,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      ...style,
    }}>
      {tabs.map(t => {
        const selected = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(t.key)}
            style={{ ...buttonReset, flex: '0 0 auto', padding: '4px 0' }}
          >
            <span style={{
              display: 'flex',
              alignItems: 'center',
              height: 36,
              padding: `0 ${space.lg - 2}px`,
              borderRadius: radius.pill,
              fontSize: font.label,
              fontWeight: weight.medium,
              whiteSpace: 'nowrap',
              color: selected ? color.textOnPrimary : color.text,
              background: selected ? color.primary : color.surface,
              boxShadow: selected ? shadow.selected : shadow.raised,
              transition: 'background 0.15s, color 0.15s',
            }}>
              {tabLabel(t)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default TabBar;
